import React, { useEffect, useState } from 'react'

import { RendererWrapper } from '@app/components/renderers'

import { cloneDeep } from '@app/utils/cloneDeep'

import { SIZE } from '@app/constants'

import { Select, notification } from 'antd'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { patchPricingLevel } from '@app/api/routes/pricing'

import { PRICING_KEY } from '@app/constants/queryKeys'

import { MANAGING_MONITORING_PRICING_LEVEL_TITLE } from '@app/api/constants'

import { PricingLevelData } from '@app/api/routes/pricing/types'

import permissionService from '@app/services/permissionService'

import { useUserQuery } from '@app/hooks'

import { Role } from '@app/types'

import { pricingLevelOptions } from '../../configs'

import { ManagingMonitoringsDataSource } from '../../types'

import type { Column } from '@app/components/base/Table/types'
import type { UsePricingLevelColumnParams } from './types'

const { hasOneOfRoles } = permissionService
const roles: Role[] = ['administrator', 'price_analytics_specialist']

const MANAGING_MONITORING_PRICING_LEVEL_TITLE_REVERSE = Object.fromEntries(
  Object.entries(MANAGING_MONITORING_PRICING_LEVEL_TITLE).map(([key, value]) => [value, key])
)

export const usePricingLevelEditableColumn = <DataSourceType extends ManagingMonitoringsDataSource>({
  columns
}: UsePricingLevelColumnParams<DataSourceType>): Array<Column<DataSourceType>> => {
  const [columnsPricingLevel, setColumnsPricingLevel] = useState(columns)

  const queryClient = useQueryClient()

  // TODO: Вынести в отдельный хук проверку роли
  const { data: user } = useUserQuery()
  let canEdit = false
  if (user?.roles) {
    canEdit = hasOneOfRoles(user.roles, roles)
  }

  const patchPricingLevelMutation = useMutation({
    mutationFn: patchPricingLevel,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [PRICING_KEY.pricingLevel] })

      notification.success({
        message: 'Успешно сохранено.'
      })
    }
  })

  useEffect(() => {
    const pricingLevelColumn = columns.find(column => column.key === 'pricingLevel')

    if (pricingLevelColumn) {
      const options = pricingLevelOptions

      const handleChange = (newVal: string, record: DataSourceType): void => {
        if (!newVal || !record) return

        const newValue = MANAGING_MONITORING_PRICING_LEVEL_TITLE_REVERSE[newVal] as
          | keyof typeof MANAGING_MONITORING_PRICING_LEVEL_TITLE
          | null
        const id = record?.id

        if (newValue && id) {
          const res: PricingLevelData = {
            productPricingGroups: [
              {
                monitoringType: id,
                pricingLevel: newValue
              }
            ]
          }

          patchPricingLevelMutation.mutate(res)
        }
      }

      pricingLevelColumn.render = (_, record) => (
        <RendererWrapper minWidth={ 200 }>
          <Select
            defaultValue={ record.pricingLevel }
            options={ options }
            onChange={ e => handleChange(e, record) }
            disabled={ !canEdit }
            size={ SIZE.small }
            showSearch
          />
        </RendererWrapper>
      )

      setColumnsPricingLevel(cloneDeep(columns))
    }
  }, [])

  return columnsPricingLevel
}
